// 演示类侧栏角标 hook（F-005/F-006/F-007）：
// 消息通知 / 风险预警 / 客服工单 等未接入业务 API 的演示角标。
// 口径与对应页面 KPI 同源：
//   - 消息通知：平台内容草稿数（GET /ops/contents · status=DRAFT）
//   - 风险预警：待审核拦截事件数（GET /risk/intercepts · status=TRIGGERED）
//   - 客服工单：待处理工单数（GET /ops/cs/tickets · status=OPEN）
//   - 通道充值 / 快捷处置：Admin 无权限，静默降级为 null
import { useEffect, useState } from 'react';
import { listContent, type ContentItem } from '@/api/opsContent';
import { listIntercepts } from '@/api/risk';
import { listTickets } from '@/api/opsTickets';

export type DemoBadges = {
  notificationDraftCount: number | null;
  riskAlertPendingCount: number | null;
  ticketOpenCount: number | null;
};

export function useDemoBadges(pathname: string): DemoBadges {
  const [notificationDraftCount, setNotificationDraftCount] = useState<number | null>(null);
  const [riskAlertPendingCount, setRiskAlertPendingCount] = useState<number | null>(null);
  const [ticketOpenCount, setTicketOpenCount] = useState<number | null>(null);

  useEffect(() => {
    let stale = false;

    // 消息通知：草稿公告数
    const loadNotifications = async () => {
      try {
        const res = await listContent('all', 'DRAFT');
        if (stale) return;
        if (!res.ok) { setNotificationDraftCount(null); return; }
        const items = (res.data as { items?: ContentItem[] }).items ?? [];
        setNotificationDraftCount(items.length);
      } catch {
        if (!stale) setNotificationDraftCount(null);
      }
    };

    // 风险预警：TRIGGERED（待审核）拦截数
    const loadRiskAlerts = async () => {
      try {
        const res = await listIntercepts({ status: 'TRIGGERED', page: 1, pageSize: 1 });
        if (stale) return;
        if (!res.ok) { setRiskAlertPendingCount(null); return; }
        setRiskAlertPendingCount(res.data.total ?? 0);
      } catch {
        if (!stale) setRiskAlertPendingCount(null);
      }
    };

    // 客服工单：OPEN 工单数
    const loadTickets = async () => {
      try {
        const res = await listTickets('OPEN', 'all');
        if (stale) return;
        if (!res.ok) { setTicketOpenCount(null); return; }
        setTicketOpenCount(res.data.items?.length ?? 0);
      } catch {
        if (!stale) setTicketOpenCount(null);
      }
    };

    // Admin 无权限访问商户通道充值，保持 null → 显示静态 badge
    // 快捷处置同理
    void loadNotifications();
    void loadRiskAlerts();
    void loadTickets();

    return () => { stale = true; };
  }, [pathname]);

  return {
    notificationDraftCount,
    riskAlertPendingCount,
    ticketOpenCount,
  };
}
